<template>
  <picture :class="`${name}__image`">
    <a-canvas-bars v-if="!loaded" />
    <source
      v-for="(item, index) in layout"
      :key="`webp_${index}`"
      :media="getMedia(item.size)"
      :srcset="getImage(item)"
      type="image/webp"
    />
    <source
      v-for="(item, index) in layout"
      :key="`jpeg_${index}`"
      :media="getMedia(item.size)"
      :srcset="getImage({ ...item, fallback: true })"
    />
    <img
      :src="getImage()"
      :alt="imageAlt"
      rel="preconnect"
      @load="stopLoader"
    />
    <slot />
  </picture>
</template>

<script>
import { mapGetters } from 'vuex'
import ACanvasBars from '~/components/atoms/a-canvas/a-canvas-bars'

export default {
  components: { ACanvasBars },
  props: {
    cms: {
      type: Object,
      default() {
        return {}
      },
    },
    src: {
      type: String,
      default() {
        return null
      },
    },
    alt: {
      type: String,
      default() {
        return 'Image'
      },
    },
    layout: {
      type: Array,
      default() {
        return null
      },
    },
    focus: {
      type: String,
      default() {
        return null
      },
    },
    smart: {
      type: String,
      default() {
        return null
      },
    },
    filter: {
      type: String,
      default() {
        return null
      },
    },
    quality: {
      type: Number,
      default() {
        return 100
      },
    },
  },
  data() {
    return {
      name: 'a-image-v2',
      loaded: false,
      mq: [
        process.env.NUXT_ENV_MEDIA_XS,
        process.env.NUXT_ENV_MEDIA_SM,
        process.env.NUXT_ENV_MEDIA_MD,
        process.env.NUXT_ENV_MEDIA_LG,
        process.env.NUXT_ENV_MEDIA_XL,
      ],
    }
  },
  computed: {
    ...mapGetters({
      loading: 'loading/isLoading',
    }),
    imageSrc() {
      if (this.cms.image2?.filename) return this.cms.image2.filename
      return (this.cms.image || {}).filename || this.cms.image || this.src
    },
    imageAlt() {
      if (this.cms.image2?.alt) return this.cms.image2.alt
      return (this.cms.image || {}).alt || this.alt
    },
  },
  watch: {
    async loading(newValue) {
      if (newValue === false && this.loaded === false) {
        await this.$store.dispatch('loading/startBusyState')
      }
    },
  },
  async beforeCreate() {
    await this.$store.dispatch('loading/startBusyState')
  },
  methods: {
    async stopLoader() {
      this.loaded = true
      await this.$store.dispatch('loading/stopBusyState')
    },
    setDefaultRatio(url) {
      try {
        for (const item of (url || '').split('/').filter(Boolean)) {
          if (item.match(/\d+x\d+/g)) {
            return item.split('x').map((x) => +x)
          }
        }
      } catch {
        return [this.width, this.height]
      }
    },
    setFilter(image, fallback = false) {
      const format = this.setFormat(image, fallback)
      const quality = this.setQuality()
      const focus = this.setFocus()
      if (format || quality) {
        return `/filters${format}${focus}${quality}`
      }
      return ''
    },
    setFocus() {
      const focus = this.focus || (this.cms.image || {}).focus || ''
      if (focus) {
        return `:focal(${focus})`
      } else {
        return ''
      }
    },
    setFormat(image, fallback = false) {
      if (image.match(/.png|.jpeg|.jpg/g) && !fallback) {
        return ':format(webp)'
      } else if (image.match(/.jpeg|.jpg/g) && fallback) {
        return ':format(jpeg)'
      } else {
        return ''
      }
    },
    setQuality() {
      if (this.quality) {
        return `:quality(${this.quality})`
      }
      return ''
    },
    getMedia(index) {
      const mq = this.mq[index]
      if (mq) return mq
    },
    getImage(options = null) {
      let width = 640
      let height = 420
      if (!this.layout) {
        return this.imageSrc
      }
      if (!options) {
        options = this.layout.slice(-1).pop()
        options.fallback = true
      }
      options = Object.assign(
        {
          bound: null,
          ratio: this.setDefaultRatio(this.imageSrc),
        },
        options
      )
      if (options.ratio[0] > options.ratio[1]) {
        // landscape
        width = options.bound
        height = this.processHeight(options.bound, options.ratio)
      } else if (options.ratio[0] < options.ratio[1]) {
        // portrait
        height = options.bound
        width = this.processWidth(options.bound, options.ratio)
      } else {
        // square
        height = options.bound
        width = options.bound
      }
      return this.imageTemplate(this.imageSrc, width, height, options.fallback)
    },
    processWidth(bound, ratio) {
      if (bound) {
        return Math.round((parseInt(bound) / ratio[1]) * ratio[0])
      }
    },
    processHeight(bound, ratio) {
      if (bound) {
        return Math.round((parseInt(bound) / ratio[0]) * ratio[1])
      }
    },
    imageTemplate(image, width, height, fallback = false) {
      if (typeof image === 'string') {
        if (!image.match(/.svg/g) && image.match(/a.storyblok.com/g)) {
          return `//img2.storyblok.com/${width}x${height}${this.setFilter(
            image,
            fallback
          )}${image.replace(/(https?:)?\/\/a.storyblok.com/, '')}`
        } else if (image.match(/dummyimage.com/g)) {
          return `//dummyimage.com/${width}x${height}/17233d/FFFFFF`
        } else {
          return this.imageSrc
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
picture {
  position: relative;
  display: block;
  overflow: hidden;
}
picture,
img {
  width: 100%;
}
</style>
